import { createSlice, isAnyOf, type PayloadAction } from '@reduxjs/toolkit';

import { scopeChanged, scopeCleared, scopeHydrated } from '@/slice/entityScopeSlice';

/** Sentinel for "no sync log exists yet" so identity comparison still works. */
export const NO_LOG_ID = 'none';

/**
 * Safety bound on the optimistic "running" marker. The spec forbids
 * wall-clock comparison (identity, never elapsed time, decides — §2 decision
 * 2), so instead of a timeout this counts distinct poll results: if the
 * backend resolves the sync request 2xx but never writes a new log row, every
 * subscriber would otherwise read "running" and poll forever. After this many
 * poll results still show the SAME log id, give up.
 *
 * The first counted result is always the sync mutation's own PmsSyncLog
 * invalidation refetch (RTK invalidates on fulfilled AND rejected mutations),
 * which lands within a second of the request; the remaining six are the
 * 10 s interval polls. So 7 ≈ one minute of waiting.
 */
export const MAX_PENDING_POLLS = 7;

/**
 * Which surface asked for the sync. The in-flight "running" state is shared by
 * every surface (all three poll the same log), but the "Property sync
 * complete." story belongs only to the surface that started it — a refresh
 * clicked on Properties must not make the settings banner or the landing hero
 * announce a completion the user never asked for there.
 */
export type SyncOrigin = 'properties' | 'settings' | 'landing';

export interface PmsSyncState {
  /**
   * Set when a sync was requested from this browser session: the id of the
   * sync log that was current at that moment. While set, every subscriber
   * reads "running" and polls, until the server shows a log with a DIFFERENT
   * id — identity, never wall-clock, decides (spec §2 decision 2).
   */
  pendingFromLogId: string | null;
  /** Surface that fired the pending sync; promoted to `startedOrigin` when the new log is observed. */
  pendingOrigin: SyncOrigin | null;
  /** The log id the last session-started sync resolved to; lets pages say "Property sync complete" for their own sync. */
  startedLogId: string | null;
  /** Surface whose sync `startedLogId` refers to. Only that surface renders "complete". */
  startedOrigin: SyncOrigin | null;
  /**
   * Distinct poll results observed while `pendingFromLogId` is set and the
   * server still reports the SAME log id (see MAX_PENDING_POLLS). Reset
   * whenever a sync starts, resolves, or is abandoned.
   */
  pendingPolls: number;
  /**
   * The most recent `getSyncLog` `fulfilledTimeStamp` already counted toward
   * `pendingPolls`. Lives here — not in a per-hook-instance ref — so that
   * multiple simultaneously-subscribed `useSyncProgress` instances observing
   * the SAME poll result only increment the counter once between them.
   */
  lastCountedPollAt: number | null;
}

const initialState: PmsSyncState = {
  pendingFromLogId: null,
  pendingOrigin: null,
  startedLogId: null,
  startedOrigin: null,
  pendingPolls: 0,
  lastCountedPollAt: null,
};

const pmsSyncSlice = createSlice({
  name: 'pmsSync',
  initialState,
  reducers: {
    /**
     * `fulfilledAt` is the `fulfilledTimeStamp` of the `getSyncLog` result
     * already in the shared cache (or `null` if none has resolved yet) at the
     * moment the sync fires. It seeds `lastCountedPollAt` so that
     * already-seen PRE-sync fetch is not counted toward MAX_PENDING_POLLS
     * when a subscribed instance's effect re-runs on the marker being set.
     * Results that land AFTER the request — starting with the mutation's own
     * invalidation refetch — carry distinct timestamps and do count.
     */
    syncRequested(
      state,
      action: PayloadAction<{ fromLogId: string; fulfilledAt: number | null; origin: SyncOrigin }>,
    ) {
      state.pendingFromLogId = action.payload.fromLogId;
      state.pendingOrigin = action.payload.origin;
      state.pendingPolls = 0;
      state.lastCountedPollAt = action.payload.fulfilledAt;
    },
    /**
     * A poll result came back still showing the log id that was current when
     * the sync started. Payload is the query's `fulfilledTimeStamp` — the
     * dedup key. Every subscribed hook instance dispatches this on every poll
     * result unconditionally; only a timestamp that differs from
     * `lastCountedPollAt` actually increments the counter, so simultaneously
     * mounted instances observing the same fetch can't double-count.
     */
    syncPollObserved(state, action: PayloadAction<number>) {
      if (action.payload !== state.lastCountedPollAt) {
        state.pendingPolls += 1;
        state.lastCountedPollAt = action.payload;
      }
    },
    /**
     * A poll result showed a log id different from the pending one — the
     * backend picked the request up. Like `syncPollObserved`, every
     * subscribed instance dispatches this from the same commit, so the
     * reducer dedups: only the first one (while a request is still pending)
     * records the story. Without the guard the second dispatch would read the
     * already-cleared `pendingOrigin` and wipe `startedOrigin` back to null.
     */
    syncObserved(state, action: PayloadAction<string>) {
      if (state.pendingFromLogId === null) return;
      state.startedLogId = action.payload;
      state.startedOrigin = state.pendingOrigin;
      state.pendingFromLogId = null;
      state.pendingOrigin = null;
      state.pendingPolls = 0;
      state.lastCountedPollAt = null;
    },
    /**
     * The surface that fired the pending sync unmounted before the backend
     * picked it up. The sync itself is left alone — it is still running and
     * every page keeps showing it — but nobody is left to own the "Property
     * sync complete." story, so `syncObserved` must not attribute it: an
     * attributed-but-orphaned story would wait for that surface's next visit
     * and announce a completion from long ago. `startedLogId` (the fact) is
     * still recorded, so PMSSync's list reset keeps working.
     */
    syncOwnerDetached(state, action: PayloadAction<SyncOrigin>) {
      if (state.pendingOrigin === action.payload) state.pendingOrigin = null;
    },
    /** The request failed fast (400/500 before any log row), or the poll count cap was hit — stop waiting. */
    syncAbandoned(state) {
      state.pendingFromLogId = null;
      state.pendingOrigin = null;
      state.pendingPolls = 0;
      state.lastCountedPollAt = null;
    },
    /**
     * Disabling an integration ends any "just completed" story for it: the
     * completed sync it referred to is no longer relevant to what the user is
     * looking at (spec: disabling must hide the sync-complete banner). Also
     * dispatched by `useSyncProgress` when the surface that started the sync
     * unmounts, so a completion line never outlives the page that asked for it.
     * Only `startedLogId` and `startedOrigin` are cleared — `pendingFromLogId` /
     * `pendingOrigin` / `pendingPolls` / `lastCountedPollAt` track an in-flight
     * sync, which neither disabling nor unmounting affects.
     */
    syncCompletionCleared(state) {
      state.startedLogId = null;
      state.startedOrigin = null;
    },
  },
  extraReducers: (builder) => {
    // Every id here is ONE account's sync log. After an account change (an
    // in-place switch, a cross-tab follow, or un-choosing the account) the next
    // log read is another account's, and any id differs from this baseline, so
    // it would read as "the sync completed" for a sync the new account never
    // ran. The old account's sync keeps running server-side; this tab just
    // stops telling its story (DV-3345 review).
    builder.addMatcher(isAnyOf(scopeChanged, scopeHydrated, scopeCleared), () => initialState);
  },
});

export const {
  syncRequested,
  syncPollObserved,
  syncObserved,
  syncOwnerDetached,
  syncAbandoned,
  syncCompletionCleared,
} = pmsSyncSlice.actions;
export const selectPmsSync = (state: { pmsSync: PmsSyncState }): PmsSyncState => state.pmsSync;
export default pmsSyncSlice.reducer;
